import React, { useContext, useEffect, useState } from 'react';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import {
  AboutYouModel,
  CallMeBackEmailContent,
  EmailResponse,
  SelectOpt
} from '../../common/CommonTypes';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import { SiteContext } from '../../../context/SiteContext';
import TextInput from '../../common/TextInput/TextInput';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import DateInput from '../../common/DateInput/DateInput';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import './NeedHelpPage.scss';
import { EmailApi } from '../../../api/EmailApi';
import {
  getDateString,
  isValidName,
  populateSelectOptions
} from '../../common/HelperMethods';
import { ValidationMessages } from '../../../consts/Common';

type NeedHelpProps = {
  showCallMeBackButton: boolean;
  showCallYouBackHeading: boolean;
};

type NeedHelpFields = {
  title: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  callDate: string | undefined;
  callTime: string;
};

type NeedHelpErrors = {
  title: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  callDate: string;
  callTime: string;
};

const initialFormValues: NeedHelpFields = {
  title: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneNumber: '',
  callDate: undefined,
  callTime: ''
};

const initialFormErrors: NeedHelpErrors = {
  title: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneNumber: '',
  callDate: '',
  callTime: ''
};

const NeedHelpPage = ({
  showCallMeBackButton,
  showCallYouBackHeading
}: NeedHelpProps): JSX.Element => {
  const { site } = useContext(SiteContext);

  const { brand } = site;

  const brandPhoneNumber = brand.brandPhoneNumbers.quotesAndRenewalsNumber;

  const [titleOptions, setTitleOptions] = useState<SelectOpt[]>([]);
  const token = site.user.token;
  const [formValues, setFormValues] =
    useState<NeedHelpFields>(initialFormValues);
  const [formErrors, setFormErrors] =
    useState<NeedHelpErrors>(initialFormErrors);
  const [showForm, setShowForm] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [poppupTitle, setPopupTitle] = useState('You missed something');
  const [popupMessage, setPopupMessage] = useState(
    'Please make sure you answer all the questions highlighted in red before you continue.'
  );
  const {
    title,
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    callDate,
    callTime
  } = formValues;

  const emailPopupTitle = 'Email submission confirmation';

  const emailSuccessMessage = 'Your request has been sent.';

  const emailFailureMessage = 'Error submitting the request.';

  useEffect(() => {
    if (titleOptions.length === 0 && showForm === true) {
      loadAboutYouDropDownList();
    }
  }, [showForm]);

  const loadAboutYouDropDownList = async () => {
    const controller = new AbortController();

    const setAboutYouDropDownlistSuccess = (aboutYouList: AboutYouModel) => {
      setTitleOptions(populateSelectOptions(aboutYouList.titles));
    };

    const setAboutYouDropDownlistFailure = (err: string) => {
      console.log(`Get title error: ${err}`);
    };

    await new CarInsuranceApi().GetAboutYouDropDownListsAsync(
      token,
      setAboutYouDropDownlistSuccess,
      setAboutYouDropDownlistFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onSelectChange = (value: string, key: string) => {
    const updatedFormValues = { ...formValues, [key]: value };

    setFormValues(updatedFormValues);
  };

  const onTextChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const updatedFormValues = { ...formValues, [key]: e.currentTarget.value };

    setFormValues(updatedFormValues);
  };

  const onDateChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const value = (e.target as HTMLInputElement).value;
    const updatedFormValues = { ...formValues, [key]: value };

    setFormValues(updatedFormValues);
  };

  const onCallMeBackClick = () => {
    if (showForm) {
      const isValid = validatePersonalDetails();

      if (isValid) {
        setShowPopup(false);
        sendEmail();
      } else {
        setShowPopup(true);
      }
    } else {
      setShowForm(true);
    }
  };

  const callTimeOptions: SelectOpt[] = [
    {
      id: '',
      value: '9AM - 12PM',
      text: '9AM - 12PM',
      disabled: false
    },
    {
      id: '',
      value: '12PM - 5PM',
      text: '12PM - 5PM',
      disabled: false
    }
  ];

  const validatePersonalDetails = (): boolean => {
    let isValid = true;

    const lastNameMinCharLimit = 3;
    const firstNameMinCharLimit = 3;
    const updatedFormErrors = { ...initialFormErrors };

    if (!SelectInputHelper.isValidValue(title)) {
      isValid = false;
      updatedFormErrors.title = 'Title is required';
    }

    if (CommonValidation.isEmpty(firstName)) {
      isValid = false;
      updatedFormErrors.firstName = 'First name is required';
    } else if (firstName.length < firstNameMinCharLimit) {
      isValid = false;
      updatedFormErrors.firstName = `First name must have at least ${firstNameMinCharLimit} characters`;
    } else {
      if (isValidName(firstName)) updatedFormErrors.firstName = '';
      else {
        updatedFormErrors.firstName =
          ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(lastName)) {
      isValid = false;
      updatedFormErrors.lastName = 'Last name is required';
    } else if (lastName.length < lastNameMinCharLimit) {
      isValid = false;
      updatedFormErrors.lastName = `Last name must have at least ${lastNameMinCharLimit} characters`;
    } else {
      if (isValidName(lastName)) updatedFormErrors.lastName = '';
      else {
        updatedFormErrors.lastName =
          ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(phoneNumber)) {
      isValid = false;
      updatedFormErrors.phoneNumber = 'Phone number is required';
    } else if (phoneNumber.length < 11) {
      isValid = false;
      updatedFormErrors.phoneNumber = 'Phone number is not valid';
    }

    if (CommonValidation.isNotEmpty(emailAddress)) {
      if (!CommonValidation.isValidEmail(emailAddress)) {
        isValid = false;
        updatedFormErrors.emailAddress =
          'Email address has an incorrect format';
      }
    } else {
      isValid = false;
      updatedFormErrors.emailAddress = 'Email addreess is required';
    }

    if (
      CommonValidation.isDateNull(callDate, true) ||
      CommonValidation.isEmpty(callDate)
    ) {
      isValid = false;
      updatedFormErrors.callDate = 'Call date is required';
    } else if (!CommonValidation.isValidDate(callDate, true)) {
      isValid = false;
      updatedFormErrors.callDate = 'Call date is invalid';
    }

    if (!SelectInputHelper.isValidValue(callTime)) {
      isValid = false;
      updatedFormErrors.callTime = 'Call time is required';
    }

    setFormErrors(updatedFormErrors);

    return isValid;
  };

  const onCloseModal = () => {
    setShowPopup(false);
    if (emailSuccess) {
      setFormValues(initialFormValues);
    }
  };

  const emailMessage = (emailSent: boolean, message: string) => {
    setShowPopup(true);
    setEmailSuccess(emailSent);
    setPopupTitle(emailPopupTitle);
    setPopupMessage(message);
  };

  const sendEmail = () => {
    const sendCallBackEmail = async () => {
      const controller = new AbortController();

      const sendEmailSuccess = (response: EmailResponse) => {
        if (response.success) {
          emailMessage(true, emailSuccessMessage);
        } else {
          emailMessage(false, emailFailureMessage);
        }
      };

      const sendEmailFailure = (response: EmailResponse) => {
        emailMessage(false, emailFailureMessage);
        console.log('error', response.errorMessages);
      };

      const callDateValue = getDateString(callDate);

      const data: CallMeBackEmailContent = {
        firstName: firstName,
        lastName: lastName,
        title: title,
        email: emailAddress,
        callDate: callDateValue,
        callTime: callTime,
        phoneNumber: phoneNumber,
        brandName: site.brand.brandName.toUpperCase()
      };

      await new EmailApi().CallMeBackEmailAsync(
        data,
        token,
        sendEmailSuccess,
        sendEmailFailure,
        controller.signal
      );

      return () => {
        controller.abort();
      };
    };

    sendCallBackEmail();
  };
  const getMinDate = () => {
    return new Date();
  };

  const handlePhoneNumberChange = (
    e: React.FormEvent<HTMLInputElement>
  ): void => {
    const numericValue = e.currentTarget.value.replace(/[^0-9]/g, '');

    const updatedFormValues = { ...formValues, ['phoneNumber']: numericValue };

    setFormValues(updatedFormValues);
  };

  return (
    <div className='need-help'>
      <div className='flex-row'>
        <h3>Need help?</h3>
      </div>
      <div className='flex-row'>
        <a href={`tel:${brandPhoneNumber}`}>{brandPhoneNumber}</a>
      </div>
      {showCallYouBackHeading && (
        <div className='flex-row'>
          <p>Please complete your details and we’ll call you back.</p>
        </div>
      )}
      {showForm && showCallMeBackButton && (
        <div>
          <div className='control'>
            <SelectInput
              name='title'
              label='Title'
              value={title}
              options={titleOptions}
              onChange={(value) => onSelectChange(value, 'title')}
              forceDefault
              addDefaultOption
              error={formErrors.title}
            />
          </div>
          <div className='control'>
            <TextInput
              name='firstname'
              label='First name'
              placeholder='First name'
              value={firstName}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onTextChange(e, 'firstName')
              }
              error={formErrors.firstName}
            />
          </div>
          <div className='control'>
            <TextInput
              name='lastname'
              label='Last name'
              placeholder='Last name'
              value={lastName}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onTextChange(e, 'lastName')
              }
              error={formErrors.lastName}
            />
          </div>
          <div className='control'>
            <TextInput
              name='email'
              label='Email address'
              placeholder='Email address'
              value={emailAddress}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                onTextChange(e, 'emailAddress')
              }
              error={formErrors.emailAddress}
            />
          </div>
          <div className='control divControlStyle'>
            <TextInput
              name='phoneNumber'
              label='Phone number'
              placeholder='Phone number'
              maxLength={11}
              value={phoneNumber}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                handlePhoneNumberChange(e)
              }
              error={formErrors.phoneNumber}
            />
          </div>
          <div className='control'>
            <DateInput
              id='call-date-id'
              name='call-date'
              label='What date would you like to schedule the call for?'
              haslabel
              value={callDate}
              useJson
              forceTimeReset
              minimumDate={getMinDate()}
              onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                onDateChange(e, 'callDate')
              }
              culture={site.cultureCode}
              error={formErrors.callDate}
            />
          </div>
          <div className='control'>
            <SelectInput
              name='callTime'
              label='What time best suits you?'
              value={callTime}
              options={callTimeOptions}
              onChange={(value) => onSelectChange(value, 'callTime')}
              forceDefault
              addDefaultOption
              error={formErrors.callTime}
            />
          </div>
        </div>
      )}
      {(!showCallMeBackButton || showForm) && (
        <div className='open-times'>
          <p>Mon–Thurs: 08.30–19.30,</p>
          <p>Fri: 09.00–18.00,</p>
          <p>Sat: 09.00–17.00</p>
        </div>
      )}
      {showCallMeBackButton && (
        <ButtonContinue
          name='button-call-me-back'
          text='Call me back'
          additionalClassNames='btn-callback'
          onClick={onCallMeBackClick}
        />
      )}
      <Modal
        role='modal-popup'
        isOpen={showPopup}
        className={`insurance-${site.brand.brandName}`}
        toggle={onCloseModal}>
        <ModalHeader toggle={onCloseModal}>{poppupTitle}</ModalHeader>
        <ModalBody>{popupMessage}</ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='btn-continue'
            text='Continue'
            onClick={onCloseModal}
            additionalClassNames='btn-ok'
          />
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default NeedHelpPage;
